import { HttpClient, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiBaseUrl } from '../../constants/common.constants';
import { IAIAgent } from '../../interfaces/ai-agent/ai-agent';
import { IAIAgentConversationHistory } from '../../interfaces/ai-agent/ai-agent-conversation-history';
import { IAIAgentCustomTool } from '../../interfaces/ai-agent/ai-agent-custom-tool.interface';
import { IAIAgentDefaultSettings } from '../../interfaces/ai-agent/ai-agent-default-settings.interface';
import { IAIAgentIndustry } from '../../interfaces/ai-agent/ai-agent-industry.interface';
import { IAIAgentRequest } from '../../interfaces/ai-agent/ai-agent-request';
import { IAIAgentSystemToolType } from '../../interfaces/ai-agent/ai-agent-system-tool-type';
import { IAIAgentTemplate } from '../../interfaces/ai-agent/ai-agent-template.interface';
import { IAskGenerativeAIKnowledge } from '../../interfaces/ai/ask-generative-ai-knowledge';
import { IActivityRequest } from '../../interfaces/common/activity-request.interface';
import { CommonRequestParams } from '../../interfaces/common/common-request-params';
import { DataSourceResponse } from '../../interfaces/data/datasourceresponse.interface';
import { GetStructResponse } from '../../interfaces/data/get-struct-response.interface';

@Injectable()
export class AIAgentService {

  private _baseUrl: string = `${ApiBaseUrl}aiagent`;

  constructor(private httpClient: HttpClient) { }

  getAllAgents(customerCode:string=""): Observable<IAIAgent[]> {
    return this.httpClient.get<IAIAgent[]>(`${this._baseUrl}/all-agents/${customerCode}`);
  }

  getAgents(request: CommonRequestParams): Observable<DataSourceResponse<IAIAgent>> {
    return this.httpClient.post<DataSourceResponse<IAIAgent>>(`${this._baseUrl}/read-agents`, request);
  }

  getAgent(id: string): Observable<IAIAgent> {
    return this.httpClient.get<IAIAgent>(`${this._baseUrl}/agent/${id}`);
  }

  getVoiceSettings(): Observable<any> {
    return this.httpClient.get<any>(`${this._baseUrl}/voice-settings`);
  }

  getVoiceIds(languageCode: string, model?: string): Observable<string[]> {
    const query = model ? `?model=${encodeURIComponent(model)}` : '';
    return this.httpClient.get<string[]>(`${this._baseUrl}/voices/${languageCode}${query}`);
  }

  getAgentDefaultValues(): Observable<IAIAgentDefaultSettings> {
    return this.httpClient.get<IAIAgentDefaultSettings>(`${this._baseUrl}/default-values`);
  }

  getKnowledgeBases(model: string): Observable<IAskGenerativeAIKnowledge[]> {
    return this.httpClient.get<IAskGenerativeAIKnowledge[]>(`${this._baseUrl}/knowledge-bases/${model}`);
  }

  createAgent(request: IAIAgentRequest): Observable<GetStructResponse<boolean>> {
    return this.httpClient.post<GetStructResponse<boolean>>(`${this._baseUrl}/create-agent`, request);
  }

  updateAgent(request: IAIAgentRequest): Observable<GetStructResponse<boolean>> {
    return this.httpClient.post<GetStructResponse<boolean>>(`${this._baseUrl}/update-agent`, request);
  }

  deleteAgent(id: string): Observable<GetStructResponse<boolean>> {
    return this.httpClient.delete<GetStructResponse<boolean>>(`${this._baseUrl}/delete-agent/${id}`);
  }

  cloneAgent(request: IAIAgentRequest): Observable<GetStructResponse<boolean>> {
    return this.httpClient.post<GetStructResponse<boolean>>(`${this._baseUrl}/clone-agent`, request);
  }

  isUniqueName(name: string): Observable<boolean> {
    return this.httpClient.get<boolean>(`${this._baseUrl}/is-unique/${name}`);
  }

  getSystemToolTypes(): Observable<IAIAgentSystemToolType[]> {
    return this.httpClient.get<IAIAgentSystemToolType[]>(`${this._baseUrl}/system-tool-types`);
  }

  getActivity(request: IActivityRequest): Observable<DataSourceResponse<IAIAgentConversationHistory>> {
    return this.httpClient.post<DataSourceResponse<IAIAgentConversationHistory>>(`${this._baseUrl}/activity`, request);
  }

  getActivityAggregate(request: IActivityRequest): Observable<DataSourceResponse<IAIAgentConversationHistory>> {
    return this.httpClient.post<DataSourceResponse<IAIAgentConversationHistory>>(`${this._baseUrl}/activity-aggregate`, request);
  }

  getConversation(id: string): Observable<IAIAgentConversationHistory> {
    return this.httpClient.get<IAIAgentConversationHistory>(`${this._baseUrl}/conversation/${id}`);
  }

  getCompleteConversation(id: string, customerCode: string = ''): Observable<IAIAgentConversationHistory> {
    return this.httpClient.get<IAIAgentConversationHistory>(`${this._baseUrl}/complete-conversation/${id}/${customerCode}`);
  }

  getIndustries(filter: string = ''): Observable<IAIAgentIndustry[]> {
    return this.httpClient.get<IAIAgentIndustry[]>(`${this._baseUrl}/categories/${filter}`);
  }

  createIndustry(item: any): Observable<boolean> {
    return this.httpClient.post<any>(`${this._baseUrl}/create-category`, item, {
      reportProgress: true,
      observe: 'events'
    }).pipe(
      map((event) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            const progress = Math.round(100 * event.loaded / event.total);
            return { status: 'progress', message: progress };

          case HttpEventType.Response:
            return event.body;

          default:
            return `Unhandled event: ${event.type}`;
        }
      })
    );
  }

  updateIndustry(item: any): Observable<boolean> {
    return this.httpClient.post<any>(`${this._baseUrl}/update-category`, item, {
      reportProgress: true,
      observe: 'events'
    }).pipe(
      map((event) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            const progress = Math.round(100 * event.loaded / event.total);
            return { status: 'progress', message: progress };

          case HttpEventType.Response:
            return event.body;

          default:
            return `Unhandled event: ${event.type}`;
        }
      })
    );
  }

  deleteIndustry(item: IAIAgentIndustry): Observable<boolean> {
    return this.httpClient.post<boolean>(`${this._baseUrl}/delete-category`, item);
  }

  updatePriority(item: IAIAgentIndustry, priority: number): Observable<boolean> {
    return this.httpClient.post<boolean>(`${this._baseUrl}/updateCategoryPriority/${priority}`, item);
  }

  //templates
  getIndustryTemplates(industryId: any): Observable<IAIAgentTemplate[]> {
    return this.httpClient.get<IAIAgentTemplate[]>(`${this._baseUrl}/templates/${industryId}`);
  }

  getTemplates(request: CommonRequestParams, industryId?: any): Observable<DataSourceResponse<IAIAgentTemplate>> {
    return this.httpClient.post<DataSourceResponse<IAIAgentTemplate>>(`${this._baseUrl}/templates/${industryId}`, request);
  }

  createTemplate(item: any): Observable<boolean> {
    return this.httpClient.post<any>(`${this._baseUrl}/create-template`, item, {
      reportProgress: true,
      observe: 'events'
    }).pipe(
      map((event) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            const progress = Math.round(100 * event.loaded / event.total);
            return { status: 'progress', message: progress };

          case HttpEventType.Response:
            return event.body;

          default:
            return `Unhandled event: ${event.type}`;
        }
      })
    );
  }

  updateTemplate(item: any): Observable<boolean> {
    return this.httpClient.post<any>(`${this._baseUrl}/update-template`, item, {
      reportProgress: true,
      observe: 'events'
    }).pipe(
      map((event) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            const progress = Math.round(100 * event.loaded / event.total);
            return { status: 'progress', message: progress };

          case HttpEventType.Response:
            return event.body;

          default:
            return `Unhandled event: ${event.type}`;
        }
      })
    );
  }


  deleteTemplate(item: IAIAgentTemplate): Observable<boolean> {
    return this.httpClient.post<boolean>(`${this._baseUrl}/delete-template`, item);
  }

  updateTemplatePriority(item: IAIAgentTemplate, priority: number): Observable<boolean> {
    return this.httpClient.post<boolean>(`${this._baseUrl}/updateTemplatePriority/${priority}`, item);
  }

  //custom tools
  getAllCustomTools(): Observable<IAIAgentCustomTool[]> {
    return this.httpClient.get<IAIAgentCustomTool[]>(`${this._baseUrl}/custom-tools`);
  }

  getCustomTool(id: string): Observable<IAIAgentCustomTool> {
    return this.httpClient.get<IAIAgentCustomTool>(`${this._baseUrl}/custom-tool/${id}`);
  }

  createCustomTool(request: IAIAgentCustomTool): Observable<GetStructResponse<string>> {
    return this.httpClient.post<GetStructResponse<string>>(`${this._baseUrl}/create-custom-tool`, request);
  }

  updateCustomTool(request: IAIAgentCustomTool): Observable<GetStructResponse<boolean>> {
    return this.httpClient.post<GetStructResponse<boolean>>(`${this._baseUrl}/update-custom-tool`, request);
  }

  deleteCustomTool(id: string): Observable<GetStructResponse<boolean>> {
    return this.httpClient.delete<GetStructResponse<boolean>>(`${this._baseUrl}/delete-custom-tool/${id}`);
  }

  cloneCustomTool(request: IAIAgentCustomTool): Observable<GetStructResponse<boolean>> {
    return this.httpClient.post<GetStructResponse<boolean>>(`${this._baseUrl}/clone-custom-tool`, request);
  }

  getCustomerCustomTools(request: CommonRequestParams): Observable<DataSourceResponse<IAIAgentCustomTool>> {
    return this.httpClient.post<DataSourceResponse<IAIAgentCustomTool>>(`${this._baseUrl}/custom-tools`, request);
  }
}
